import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/95 backdrop-blur-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-2 group"
          >
            <div className="relative w-24 h-24 md:w-32 md:h-32">
              <img
                src="/Dis-removebg-preview.png"
                alt="Distrotopia Logo"
                className="w-full h-full object-contain transform transition-transform group-hover:rotate-12"
              />
              <div className="absolute inset-0 bg-purple-500/20 blur-lg rounded-full animate-pulse"></div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {['/', '/pricing', '/terms'].map((path) => (
              <Link
                key={path}
                to={path}
                className={`relative px-1 py-2 text-sm font-medium transition-colors ${
                  isActive(path) ? 'text-purple-400' : 'text-white hover:text-purple-400'
                }`}
              >
                <span className="relative z-10">
                  {path === '/' ? 'Home' : path.slice(1).charAt(0).toUpperCase() + path.slice(2)}
                </span>
                {isActive(path) && (
                  <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-purple-500 to-purple-400 transform origin-left"></div>
                )}
              </Link>
            ))}
            <Link 
              to="/auth"
              className="bg-gradient-to-r from-purple-500 to-purple-700 px-6 py-2 rounded-full text-white font-medium hover:from-purple-600 hover:to-purple-800 transition-all transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25"
            >
              Sign In
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg text-white hover:bg-purple-500/20 transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`md:hidden fixed inset-0 bg-black/95 backdrop-blur-md transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ top: '5rem' }}
      >
        <div className="p-4 space-y-4">
          {['/', '/pricing', '/terms'].map((path) => (
            <Link
              key={path}
              to={path}
              onClick={() => setIsOpen(false)}
              className={`block px-4 py-3 rounded-lg text-center transition-colors ${
                isActive(path) 
                  ? 'bg-purple-500/20 text-purple-400' 
                  : 'text-white hover:bg-purple-500/10'
              }`}
            >
              {path === '/' ? 'Home' : path.slice(1).charAt(0).toUpperCase() + path.slice(2)}
            </Link>
          ))}
          <Link 
            to="/auth"
            onClick={() => setIsOpen(false)}
            className="block w-full bg-gradient-to-r from-purple-500 to-purple-700 px-6 py-3 rounded-lg text-white font-medium hover:from-purple-600 hover:to-purple-800 transition-all text-center"
          >
            Sign In
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;